import { ApiError } from "../../CustomerApiService";

/**
 * The error code `apps/customer-api/src/routes/spotter/tier.ts`'s `requireSpotterAccess` sets
 * (`SPOTTER_ACCESS_DENIED_CODE`) on a 403 — MUST stay in sync with that constant (there is no
 * shared package between customer-api and customer-frontend to import it from directly).
 */
const SPOTTER_ACCESS_DENIED_CODE = "spotter_access_denied";

/**
 * True when `error` is an `ApiError` carrying the Spotter-specific access-denied code — used by
 * each Spotter screen (e.g. `fields/FieldsScreen.tsx`) to show its own
 * `SPOTTER_STRINGS.<feature>.accessDeniedError` instead of the generic `loadError` banner when a
 * query fails specifically because the caller isn't in the configured ACL group (round 17 — see
 * `tier.ts`'s doc comment for the full access-control model).
 */
export function isSpotterAccessDeniedError(error: unknown): boolean {
  return error instanceof ApiError && error.code === SPOTTER_ACCESS_DENIED_CODE;
}

/** One query's error state, as `react-query`'s `UseQueryResult` shape already provides it — kept
 *  minimal so callers can pass a `useQuery` result straight through. */
export interface QueryErrorState {
  isError: boolean;
  error: unknown;
}

/**
 * Combines several queries' error states into the one "does THIS screen show its error banner,
 * and is it the access-denied variant" decision every multi-query Spotter screen makes (e.g.
 * `fields/FieldsScreen.tsx`, `facialRecognition/FrScreen.tsx`, `rename/RenameScreen.tsx`).
 * Pulled out standalone — each screen used to inline this as an `||` chain across its own list of
 * queries, which is exactly how `FieldsScreen` ended up with a parity bug (its `isLoading` gate
 * listed 4 queries, its error gate only 3 — a failure isolated to the 4th silently rendered as if
 * it had succeeded). One shared implementation means there's one place to get this right, and it
 * can be unit-tested without rendering a screen.
 */
export function combineSpotterQueryErrors(queries: QueryErrorState[]): {
  hasError: boolean;
  accessDenied: boolean;
} {
  return {
    hasError: queries.some((q) => q.isError),
    accessDenied: queries.some((q) => isSpotterAccessDeniedError(q.error)),
  };
}
